import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js'; // New import
import { supabaseUrl, supabaseKey } from '../config.ts';
const supabase = createClient(supabaseUrl, supabaseKey);

const AreaCalculator = () => {
    const [shape, setShape] = useState('Triangle'); // Default to Triangle as per image
    const [side1, setSide1] = useState('');
    const [side2, setSide2] = useState('');
    const [side3, setSide3] = useState('');
    const [unit1, setUnit1] = useState('Feet');
    const [unit2, setUnit2] = useState('Feet');
    const [unit3, setUnit3] = useState('Feet');
    const [targetUnit, setTargetUnit] = useState('Sq Inches');
    const [calculatedArea, setCalculatedArea] = useState(null);
    const [formulaText, setFormulaText] = useState(null);
    const [error, setError] = useState('');

    // New state variables for feedback popup
    const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
    const [feedbackText, setFeedbackText] = useState('');
    const [feedbackRating, setFeedbackRating] = useState(5); // Default rating
    const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
    const [feedbackError, setFeedbackError] = useState('');

    const lengthUnits = ['Inches', 'Feet', 'Yards', 'Miles', 'Meters', 'Kilometers', 'Centimeters'];
    const areaUnits = ['Sq Inches', 'Sq Feet', 'Sq Yards', 'Sq Miles', 'Sq Meters', 'Sq Kilometers', 'Sq Centimeters'];

    // Conversion factors to a base unit (Feet)
    const toFeet = {
        'Inches': 1 / 12,
        'Feet': 1,
        'Yards': 3,
        'Miles': 5280, // 1 mile = 5280 feet
        'Meters': 3.28084,
        'Kilometers': 3280.84, // 1 kilometer = 3280.84 feet
        'Centimeters': 0.0328084,
    };

    // Conversion factors from base unit (Sq Feet)
    const fromSqFeet = {
        'Sq Inches': 144,
        'Sq Feet': 1,
        'Sq Yards': 1 / 9,
        'Sq Miles': 1 / (5280 * 5280), // 1 sq mile = 5280*5280 sq feet
        'Sq Meters': 0.092903,
        'Sq Kilometers': 1 / (3280.84 * 3280.84), // 1 sq km = 3280.84*3280.84 sq feet
        'Sq Centimeters': 929.03,
    };

    const convertToFeet = (value, unit) => {
        return parseFloat(value) * toFeet[unit];
    };

    const convertFromSqFeet = (areaSqFeet, unit) => {
        return areaSqFeet * fromSqFeet[unit];
    };

    // Effect to update formula text and reset inputs when shape changes
    useEffect(() => {
        setCalculatedArea(null);
        setError('');
        setSide1('');
        setSide2('');
        setSide3('');
        setUnit1('Feet');
        setUnit2('Feet');
        setUnit3('Feet');
        // Also reset feedback states when shape changes
        setShowFeedbackPopup(false);
        setFeedbackText('');
        setFeedbackRating(5);
        setFeedbackSubmitted(false);
        setFeedbackError('');

        switch (shape) {
            case 'Rectangle':
                setFormulaText(
                    <p className="text-warning mb-0">Area = Length × Width</p>
                );
                break;
            case 'Triangle':
                setFormulaText(
                    <>
                        <p className="text-warning mb-2">Triangle area calculated using the Heron's formula:</p>
                        <p className="text-warning mb-1">
                            sp (semiperimeter) = <span className="d-inline-block border-bottom border-warning pb-1">s1 + s2 + s3</span> / 2
                        </p>
                        <p className="text-warning mb-0">
                            Area = &radic;<span className="text-decoration-underline">sp</span> X (sp-s1) X (sp-s2) X (sp-s3)
                        </p>
                    </>
                );
                break;
            case 'Trapezoid':
                setFormulaText(
                    <p className="text-warning mb-0">Area = 0.5 × (Base 1 + Base 2) × Height</p>
                );
                break;
            case 'Parallelogram':
                setFormulaText(
                    <p className="text-warning mb-0">Area = Base × Height</p>
                );
                break;
            default:
                setFormulaText(null);
        }
    }, [shape]); // Dependency array: runs when 'shape' state changes

    const calculateArea = () => {
        setError('');
        setCalculatedArea(null);
        setFeedbackSubmitted(false); // Reset feedback status for new calculation

        let areaSqFeet = 0;
        // Convert all potential inputs to feet. If input is empty or invalid, convertToFeet will return NaN.
        let s1_ft = convertToFeet(side1, unit1);
        let s2_ft = convertToFeet(side2, unit2);
        let s3_ft = convertToFeet(side3, unit3);

        // The specific input validation for each shape is now handled within its case.
        // This makes the validation clearer and more precise.
        switch (shape) {
            case 'Rectangle':
                if (isNaN(s1_ft) || isNaN(s2_ft)) {
                    setError('Please enter valid numeric values for Length and Width.');
                    return;
                }
                areaSqFeet = s1_ft * s2_ft;
                break;
            case 'Triangle':
                if (isNaN(s1_ft) || isNaN(s2_ft) || isNaN(s3_ft)) {
                    setError('Please enter valid numeric values for all three sides.');
                    return;
                }
                // Heron's formula
                if (s1_ft + s2_ft <= s3_ft || s1_ft + s3_ft <= s2_ft || s2_ft + s3_ft <= s1_ft) {
                    setError('Invalid triangle sides. The sum of any two sides must be greater than the third side.');
                    return;
                }
                const s = (s1_ft + s2_ft + s3_ft) / 2; // semi-perimeter
                areaSqFeet = Math.sqrt(s * (s - s1_ft) * (s - s2_ft) * (s - s3_ft));
                break;
            case 'Trapezoid':
                // Assuming Side-1 and Side-2 are parallel bases, Side-3 is height
                if (isNaN(s1_ft) || isNaN(s2_ft) || isNaN(s3_ft)) {
                    setError('Please enter valid numeric values for Base 1, Base 2, and Height.');
                    return;
                }
                areaSqFeet = 0.5 * (s1_ft + s2_ft) * s3_ft;
                break;
            case 'Parallelogram':
                // Assuming Side-1 is base, Side-2 is height
                if (isNaN(s1_ft) || isNaN(s2_ft)) {
                    setError('Please enter valid numeric values for Base and Height.');
                    return;
                }
                areaSqFeet = s1_ft * s2_ft;
                break;
            default:
                setError('Please select a shape.');
                return;
        }

        if (isNaN(areaSqFeet) || areaSqFeet < 0) {
            setError('Could not calculate area. Please check your inputs.');
            return;
        }

        const finalArea = convertFromSqFeet(areaSqFeet, targetUnit);
        setCalculatedArea(finalArea.toFixed(2));

        // Show feedback popup after 2 seconds
        setTimeout(() => {
            setShowFeedbackPopup(true);
        }, 2000);
    };

    const handleSubmitFeedback = async () => {
        setFeedbackError('');
        // If no text is provided AND the rating is still the default (5), prompt for input.
        // This encourages interaction but allows submitting just a rating or just text.
        if (!feedbackText.trim() && feedbackRating === 5) {
            setFeedbackError('Please provide some comments or adjust the rating.');
            return;
        }

        try {
            const { data, error } = await supabase
                .from('feedback')
                .insert([
                    { comment: feedbackText, rating: feedbackRating }
                ]);

            if (error) {
                throw error;
            }

            setFeedbackSubmitted(true);
            // Optionally, close popup after a short delay or immediately
            setTimeout(() => {
                setShowFeedbackPopup(false);
                setFeedbackText('');
                setFeedbackRating(5);
                setFeedbackSubmitted(false);
            }, 1500); // Close after 1.5 seconds
        } catch (error) {
            console.error('Error submitting feedback:', error.message);
            setFeedbackError('Failed to submit feedback. Please try again.');
        }
    };

    const handleCloseFeedback = () => {
        setShowFeedbackPopup(false);
        setFeedbackText('');
        setFeedbackRating(5);
        setFeedbackSubmitted(false);
        setFeedbackError('');
    };

    const resetForm = () => {
        setShape('Triangle'); // This will trigger the useEffect to reset other states
        setTargetUnit('Sq Inches');
        setCalculatedArea(null);
        setError('');
        // Ensure feedback popup is hidden and states are reset on form reset
        setShowFeedbackPopup(false);
        setFeedbackText('');
        setFeedbackRating(5);
        setFeedbackSubmitted(false);
        setFeedbackError('');
    };

    return (
        <>
            {/* Header Start */}
            <div className="container-fluid bg-breadcrumb">
                <ul className="breadcrumb-animation">
                    <li />
                    <li />
                    <li />
                    <li />
                    <li />
                    <li />
                    <li />
                    <li />
                    <li />
                    <li />
                </ul>
                <div className="container text-center py-5" style={{ maxWidth: 900 }}>
                    <h3 className="display-3 mb-4 wow fadeInDown" data-wow-delay="0.1s">
                        Area Calculator
                    </h3>
                    <ol
                        className="breadcrumb justify-content-center mb-0 wow fadeInDown"
                        data-wow-delay="0.3s"
                    >
                        <li className="breadcrumb-item">
                            <a href="/">Home</a>
                        </li>
                        <li className="breadcrumb-item active text-primary">Calculator</li>
                    </ol>
                </div>
            </div>
            {/* Header End */}

            <div className="container py-5">
                <div className="row justify-content-center">
                    <div className="col-lg-8">
                        <div className="card shadow rounded-4 p-4">
                            <h4 className="mb-4 text-center">Calculate Area for a Plot / Land</h4>
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}
                            <div className="mb-3 row align-items-center">
                                <label htmlFor="areaShape" className="col-sm-4 col-form-label">Area Shape:</label>
                                <div className="col-sm-8">
                                    <select
                                        id="areaShape"
                                        className="form-select"
                                        value={shape}
                                        onChange={(e) => setShape(e.target.value)}
                                    >
                                        <option value="Rectangle">Rectangle</option>
                                        <option value="Triangle">Triangle</option>
                                        <option value="Trapezoid">Trapezoid</option>
                                        <option value="Parallelogram">Parallelogram</option>
                                    </select>
                                </div>
                            </div>

                            <div className="mb-3 row align-items-center">
                                <label htmlFor="side1" className="col-sm-4 col-form-label">
                                    {shape === 'Rectangle' ? 'Length:' :
                                     shape === 'Parallelogram' ? 'Base:' :
                                     shape === 'Trapezoid' ? 'Base 1:' :
                                     'Side 1:'}
                                </label>
                                <div className="col-sm-5">
                                    <input
                                        type="number"
                                        className="form-control"
                                        id="side1"
                                        value={side1}
                                        onChange={(e) => setSide1(e.target.value)}
                                        placeholder={shape === 'Rectangle' ? 'Length' :
                                                     shape === 'Parallelogram' ? 'Base' :
                                                     shape === 'Trapezoid' ? 'Base 1' :
                                                     'Side 1'}
                                    />
                                </div>
                                <div className="col-sm-3">
                                    <select
                                        className="form-select"
                                        value={unit1}
                                        onChange={(e) => setUnit1(e.target.value)}
                                    >
                                        {lengthUnits.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                                    </select>
                                </div>
                            </div>

                            {/* Conditional rendering for Side-2 and Side-3 based on shape */}
                            {['Rectangle', 'Triangle', 'Trapezoid', 'Parallelogram'].includes(shape) && (
                                <div className="mb-3 row align-items-center">
                                    <label htmlFor="side2" className="col-sm-4 col-form-label">
                                        {shape === 'Rectangle' ? 'Width:' :
                                         shape === 'Parallelogram' ? 'Height:' :
                                         shape === 'Trapezoid' ? 'Base 2:' :
                                         'Side 2:'}
                                    </label>
                                    <div className="col-sm-5">
                                        <input
                                            type="number"
                                            className="form-control"
                                            id="side2"
                                            value={side2}
                                            onChange={(e) => setSide2(e.target.value)}
                                            placeholder={shape === 'Rectangle' ? 'Width' :
                                                         shape === 'Parallelogram' ? 'Height' :
                                                         shape === 'Trapezoid' ? 'Base 2' :
                                                         'Side 2'}
                                        />
                                    </div>
                                    <div className="col-sm-3">
                                        <select
                                            className="form-select"
                                            value={unit2}
                                            onChange={(e) => setUnit2(e.target.value)}
                                        >
                                            {lengthUnits.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                                        </select>
                                    </div>
                                </div>
                            )}

                            {(shape === 'Triangle' || shape === 'Trapezoid') && (
                                <div className="mb-3 row align-items-center">
                                    <label htmlFor="side3" className="col-sm-4 col-form-label">
                                        {shape === 'Trapezoid' ? 'Height:' : 'Side 3:'}
                                    </label>
                                    <div className="col-sm-5">
                                        <input
                                            type="number"
                                            className="form-control"
                                            id="side3"
                                            value={side3}
                                            onChange={(e) => setSide3(e.target.value)}
                                            placeholder={shape === 'Trapezoid' ? 'Height' : 'Side 3'}
                                        />
                                    </div>
                                    <div className="col-sm-3">
                                        <select
                                            className="form-select"
                                            value={unit3}
                                            onChange={(e) => setUnit3(e.target.value)}
                                        >
                                            {lengthUnits.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                                        </select>
                                    </div>
                                </div>
                            )}

                            <div className="mb-4 row align-items-center">
                                <label htmlFor="targetUnit" className="col-sm-4 col-form-label">Target Unit of Measurement:</label>
                                <div className="col-sm-8">
                                    <select
                                        id="targetUnit"
                                        className="form-select"
                                        value={targetUnit}
                                        onChange={(e) => setTargetUnit(e.target.value)}
                                    >
                                        {areaUnits.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="d-flex justify-content-center gap-3 mb-4">
                                <button className="btn btn-success px-4" onClick={calculateArea}>Calculate</button>
                                <button className="btn btn-warning px-4" onClick={resetForm}>Reset</button>
                            </div>

                            {/* This card will always show the formula, and the result if calculated */}
                            <div className="card bg-dark text-white p-4 rounded-4 mt-4">
                                {calculatedArea !== null && (
                                    <>
                                        <div className="d-flex justify-content-center align-items-baseline mb-3">
                                            <span className="text-warning me-2" style={{ fontSize: '1.2rem' }}>Total Area :</span>
                                            <strong className="text-white me-1" style={{ fontSize: '2.5rem' }}>{calculatedArea}</strong>
                                            <span className="text-white" style={{ fontSize: '1.2rem' }}>{targetUnit}</span>
                                        </div>
                                        <hr className="border-secondary my-3" />
                                    </>
                                )}
                                {formulaText && (
                                    <div className="text-center">
                                        {formulaText}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Feedback Popup Modal */}
            {showFeedbackPopup && (
                <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
            )}
            {showFeedbackPopup && (
                <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    role="dialog"
                    style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
                >
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content bg-dark text-white">
                            <div className="modal-header border-bottom-0">
                                <h5 className="modal-title">Provide Feedback</h5>
                                <button type="button" className="btn-close btn-close-white" aria-label="Close" onClick={handleCloseFeedback}></button>
                            </div>
                            <div className="modal-body">
                                {!feedbackSubmitted ? (
                                    <>
                                        {feedbackError && <div className="alert alert-danger">{feedbackError}</div>}
                                        <div className="mb-3">
                                            <label htmlFor="feedbackText" className="form-label">Comments (Optional):</label>
                                            <textarea
                                                className="form-control bg-light text-dark border rounded-3 shadow-sm" // Updated styling
                                                id="feedbackText"
                                                rows="3"
                                                value={feedbackText}
                                                onChange={(e) => setFeedbackText(e.target.value)}
                                                placeholder="Tell us what you think..."
                                            ></textarea>
                                        </div>
                                        <div className="mb-3 text-center"> {/* Centered for star rating */}
                                            <label className="form-label d-block mb-2">Rating:</label>
                                            <div className="star-rating">
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                    <span
                                                        key={star}
                                                        className="star"
                                                        style={{
                                                            cursor: 'pointer',
                                                            fontSize: '2rem', // Larger stars
                                                            color: star <= feedbackRating ? '#ffc107' : '#e4e5e9', // Gold for filled, light gray for empty
                                                            transition: 'color 0.2s ease-in-out', // Smooth transition
                                                        }}
                                                        onClick={() => setFeedbackRating(star)}
                                                    >
                                                        &#9733; {/* Unicode star character */}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-center text-success">
                                        <p className="mb-0">Thank you for your feedback!</p>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer border-top-0">
                                {!feedbackSubmitted && (
                                    <>
                                        <button type="button" className="btn btn-secondary" onClick={handleCloseFeedback}>
                                            Cancel
                                        </button>
                                        <button type="button" className="btn btn-primary" onClick={handleSubmitFeedback}>
                                            Submit Feedback
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AreaCalculator;